type ListingPageParam = string | string[] | undefined;

export type ListingPageParseResult =
  | { status: 'default'; page: 1 }
  | { status: 'redirect_first' }
  | { status: 'ok'; page: number }
  | { status: 'invalid' };

const toRawPageParam = (value: ListingPageParam): string | undefined => {
  if (Array.isArray(value)) {
    return typeof value[0] === 'string' ? value[0] : undefined;
  }
  return typeof value === 'string' ? value : undefined;
};

/**
 * Parses listing `page` query:
 * - missing → page 1 (default, no redirect)
 * - page=1 → permanent redirect to URL without page
 * - integer ≥ 2 → ok
 * - 0, negative, fractional, non-numeric → invalid (404)
 */
export const parseListingPageParam = (value: ListingPageParam): ListingPageParseResult => {
  const rawValue = toRawPageParam(value);
  if (rawValue === undefined || rawValue.trim() === '') {
    return { status: 'default', page: 1 };
  }

  const trimmed = rawValue.trim();
  if (trimmed === '1') {
    return { status: 'redirect_first' };
  }

  if (!/^[1-9]\d*$/.test(trimmed)) {
    return { status: 'invalid' };
  }

  return { status: 'ok', page: Number.parseInt(trimmed, 10) };
};

export const isListingPageOutOfRange = (page: number, totalPages: number): boolean => {
  if (page <= 1) return false;
  if (totalPages < 1) return page > 1;
  return page > totalPages;
};

const LISTING_QUERY_KEYS = [
  'page',
  'sort',
  'search',
  'fandoms',
  'tags',
  'universes',
  'statuses',
  'ratings',
  'characters',
  'pairingCharacters',
] as const;

/**
 * Builds pathname+query without `page` (for 301 from ?page=1).
 */
export const buildListingHrefWithoutPage = (
  pathname: string,
  searchParams: Record<string, string | string[] | undefined>,
): string => {
  const query = new URLSearchParams();
  for (const key of LISTING_QUERY_KEYS) {
    if (key === 'page') continue;
    const value = searchParams[key];
    if (value === undefined) continue;
    const stringValue = Array.isArray(value) ? value[0] : value;
    if (typeof stringValue === 'string' && stringValue.length > 0) {
      query.set(key, stringValue);
    }
  }
  const queryString = query.toString();
  return queryString ? `${pathname}?${queryString}` : pathname;
};

/**
 * Builds a listing href for pagination links, preserving filters/sort/search.
 */
export const buildListingPaginationHref = (
  pathname: string,
  searchParams: Record<string, string | string[] | undefined>,
  page: number,
): string => {
  const query = new URLSearchParams();
  for (const key of LISTING_QUERY_KEYS) {
    if (key === 'page') continue;
    const value = searchParams[key];
    if (value === undefined) continue;
    const stringValue = Array.isArray(value) ? value[0] : value;
    if (typeof stringValue === 'string' && stringValue.length > 0) {
      if (key === 'sort' && stringValue === 'hotNew') continue;
      query.set(key, stringValue);
    }
  }
  if (page > 1) {
    query.set('page', String(page));
  }
  const queryString = query.toString();
  return queryString ? `${pathname}?${queryString}` : pathname;
};

export const LISTING_PAGE_SUFFIX = (page: number): string =>
  page > 1 ? ` — страница ${page}` : '';
